// src/features/timetracking/export/ttExport.ts
// Vollständig: Fetch der TTs (gemäß deiner API), Berechnung Nettozeiten, CSV/XLSX/PDF-Export + Download.

import TimeTrackingService from '../services/TimeTrackingService';
export type BreakPeriod = { id:number; start_time:string; end_time:string };
export type TTRecord = {
  id: number;
  employee: number | string;
  work_area: number;
  work_area__name?: string;
  start_time: string;  // ISO UTC
  end_time: string;    // ISO UTC
  breakperiods?: BreakPeriod[];
  has_pending_change_request?: boolean;
  active_change_request?: unknown;
};

export type FetchParams = {
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
  work_area_ids?: number[];   // optional
  employee?: number | string; // optional
};

export type ExportRow = {
  employee_id: number | string;
  employee_name: string;
  work_area: string;
  day: string;          // YYYY-MM-DD
  start_local: string;  // e.g. "09:00"
  end_local: string;    // e.g. "17:00"
  break_minutes: number;
  net_minutes: number;
  net_hhmm: string;     // "7:30"
  tt_id: number;
};

export type ExportOptions = {
  tenantTimeZone?: string;               // falls vorhanden -> lokale Anzeige
  employeeNameById?: (id:number|string)=>string|undefined; // Name-Mapping
  csvDelimiter?: ',' | ';';              // Default: ';' (DE/Excel)
  excelIncludeSummarySheet?: boolean;    // Default: true
  pdfTitle?: string;                     // Default: "Time Trackings"
  fileBaseName?: string;                 // ohne Endung
  maxPdfRows?: number;                   // Schutz gegen Riesendateien (Default 1500)
};

function toLocal(dtIso: string, tz?: string, withDate=false) {
  const d = new Date(dtIso);
  const opts: Intl.DateTimeFormatOptions = withDate
    ? { timeZone: tz, hour: '2-digit', minute: '2-digit', year:'numeric', month:'2-digit', day:'2-digit' }
    : { timeZone: tz, hour: '2-digit', minute: '2-digit' };
  return new Intl.DateTimeFormat(undefined, opts).format(d);
}
function toLocalDate(dtIso: string, tz?: string) {
  const d = new Date(dtIso);
  const opts: Intl.DateTimeFormatOptions = { timeZone: tz, year:'numeric', month:'2-digit', day:'2-digit' };
  return new Intl.DateTimeFormat('en-CA', opts).format(d); // "YYYY-MM-DD" stabil
}
function minutesBetween(aIso: string, bIso: string): number {
  // robust über DST, via UTC-Millis
  const a = new Date(aIso).getTime();
  const b = new Date(bIso).getTime();
  return Math.max(0, Math.round((b - a) / 60000));
}
function sumBreakMinutes(breaks?: BreakPeriod[]): number {
  if (!breaks || breaks.length===0) return 0;
  return breaks.reduce((acc, p) => acc + minutesBetween(p.start_time, p.end_time), 0);
}
function toHHMM(mins: number): string {
  const h = Math.floor(mins/60);
  const m = mins % 60;
  return `${h}:${m.toString().padStart(2,'0')}`;
}

export async function fetchTimeTrackings(params: FetchParams): Promise<TTRecord[]> {
  const options: any = {
    start_date: params.start_date,
    end_date: params.end_date
  };
  if (params.work_area_ids && params.work_area_ids.length > 0) {
    options.work_area = params.work_area_ids.join(',');
  }
  if (params.employee !== undefined && params.employee !== null) {
    options.employee = params.employee;
  }
  const data = await TimeTrackingService.fetchTimeTrackings(options);
  return data as unknown as TTRecord[];
}

export function toExportRows(records: TTRecord[], opt?: ExportOptions): ExportRow[] {
  const tz = opt?.tenantTimeZone;
  const nameOf = opt?.employeeNameById;
  return records.map(r => {
    const breaks = sumBreakMinutes(r.breakperiods);
    const gross = minutesBetween(r.start_time, r.end_time);
    const net = Math.max(0, gross - breaks);
    const day = toLocalDate(r.start_time, tz);
    return {
      employee_id: r.employee,
      employee_name: (nameOf?.(r.employee) ?? `#${r.employee}`),
      work_area: r.work_area__name ?? String(r.work_area),
      day,
      start_local: toLocal(r.start_time, tz),
      end_local: toLocal(r.end_time, tz),
      break_minutes: breaks,
      net_minutes: net,
      net_hhmm: toHHMM(net),
      tt_id: r.id
    };
  }).sort((a,b) => (a.employee_name.localeCompare(b.employee_name) || a.day.localeCompare(b.day) || a.start_local.localeCompare(b.start_local)));
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---------------- CSV ---------------- */
export function exportCSV(rows: ExportRow[], opt?: ExportOptions) {
  const sep = opt?.csvDelimiter ?? ';';
  const header = [
    'Employee ID', 'Employee Name', 'Work Area', 'Day',
    'Start', 'End', 'Break (min)', 'Net (min)', 'Net (hh:mm)', 'TT ID'
  ];
  const lines = [header.join(sep)];
  for (const r of rows) {
    const cells = [
      r.employee_id, safeCsv(r.employee_name), safeCsv(r.work_area), r.day,
      r.start_local, r.end_local, r.break_minutes, r.net_minutes, r.net_hhmm, r.tt_id
    ];
    lines.push(cells.join(sep));
  }
  const content = '\ufeff' + lines.join('\r\n'); // BOM für Excel
  downloadBlob(new Blob([content], { type:'text/csv;charset=utf-8' }), `${baseName(opt)}.csv`);
}
function safeCsv(v:any){ const s=String(v??''); return (s.includes(';')||s.includes(',')||s.includes('"')||s.includes('\n')) ? `"${s.replace(/"/g,'""')}"` : s; }
function baseName(opt?:ExportOptions){
  const now = new Date();
  const stamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  return opt?.fileBaseName ? `${opt.fileBaseName}_${stamp}` : `time-trackings_${stamp}`;
}

/* ---------------- Excel (.xlsx) ---------------- */
export async function exportXLSX(rows: ExportRow[], opt?: ExportOptions) {
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('TimeTrackings');
  const worksheetData = [
    ['Employee ID','Employee Name','Work Area','Day','Start','End','Break (min)','Net (min)','Net (hh:mm)','TT ID'],
    ...rows.map(r => [r.employee_id, r.employee_name, r.work_area, r.day, r.start_local, r.end_local, r.break_minutes, r.net_minutes, r.net_hhmm, r.tt_id])
  ];
  ws.addRows(worksheetData);

  if (opt?.excelIncludeSummarySheet !== false) {
    const byEmp = new Map<string, number>();
    rows.forEach(r => {
      const key = `${r.employee_name} (${r.employee_id})`;
      byEmp.set(key, (byEmp.get(key) ?? 0) + r.net_minutes);
    });
    const summary: (string|number)[][] = [[ 'Employee', 'Total (min)', 'Total (hh:mm)' ]];
    Array.from(byEmp.entries()).forEach(([emp, minutes]) => {
      summary.push([emp, minutes, toHHMM(minutes)]);
    });
    summary.push(['—', '—', '—']);
    const totalMin = rows.reduce((acc, r)=>acc + r.net_minutes, 0);
    summary.push(['ALL', totalMin, toHHMM(totalMin)]);
    const wsSum = wb.addWorksheet('Summary');
    wsSum.addRows(summary);
  }

  const buf = await wb.xlsx.writeBuffer();
  downloadBlob(new Blob([buf], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${baseName(opt)}.xlsx`);
}

/* ---------------- PDF ---------------- */
export async function exportPDF(rows: ExportRow[], opt?: ExportOptions) {
  const maxRows = opt?.maxPdfRows ?? 1500;
  if (rows.length > maxRows) {
    throw new Error(`Zu viele Zeilen (${rows.length}). Bitte CSV/Excel nutzen oder Zeitraum einschränken.`);
  }
  const { default: jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const title = opt?.pdfTitle ?? 'Time Trackings';
  doc.setFontSize(14);
  doc.text(title, 40, 30);

  const head = [['Employee','Work Area','Day','Start','End','Break (min)','Net (min)','Net (hh:mm)']];
  const body = rows.map(r => [ `${r.employee_name} (#${r.employee_id})`, r.work_area, r.day, r.start_local, r.end_local, String(r.break_minutes), String(r.net_minutes), r.net_hhmm ]);
  const totalMin = rows.reduce((acc, r)=> acc + r.net_minutes, 0);
  const foot: any[] = [[{ content: 'Summe', colSpan: 6, styles: { halign: 'right' as const } }, String(totalMin), toHHMM(totalMin) ]];

  autoTable(doc, {
    head, body,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [15, 43, 90], textColor: 255 },
    startY: 50,
    theme: 'grid',
    foot,
    // Zeige Fuß nur auf der letzten Seite
    showFoot: 'lastPage' as any,
    didDrawPage: (data: any) => {
      const page = doc.getNumberOfPages();
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.getWidth();
      doc.setFontSize(9);
      doc.text(`Seite ${page}`, pageWidth - 60, pageSize.getHeight() - 20);
    }
  });

  doc.save(`${baseName(opt)}.pdf`);
}
